import keycloak, { resetAndReinitializeKeycloak } from '@auth/keycloak';
import { KeycloakUserInfo } from '@auth/models/UserInfo.model';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser';
import { storageService } from '@shared/services/storage.service';
import { AuthActions } from '@shared/store/auth/auth';
import { KeycloakTokenParsed } from 'keycloak-js';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { KEYCLOAK_PROVIDER_INIT_OPTIONS } from './constants/capacitotKeycloakInitOptions.ts';
import { ONE_YEAR_MS, THIRTY_DAYS_MS, THIRTY_MINUTES_MS } from './constants/expiration-durations.ts';
import { IN_APP_BROWSER_OPTIONS } from './constants/inAppBrowserOptions.ts';
import { generateLoginUrl } from './utils/generateLoginUrl.ts';
import { generateLogoutUrl } from './utils/generateLogoutUrl.ts';
import { parseJwt } from './utils/parseJWT.ts';
import { generatePKCE } from './utils/pkce.ts';
import { exchangeCodeForTokens, refreshAccessToken } from './utils/tokenExchange.ts';
import { extractOAuthParams } from './utils/urlHandling.ts';

export const useKeycloakAuthCapacitor = () => {
  const dispatch = useDispatch();

  // Handle successful authentication by loading user info and updating Redux state
  const onAuthSuccess = useCallback(async () => {
    const userInfo = await keycloak.loadUserInfo();

    // Store tokens for persistence
    if (keycloak.token && keycloak.refreshToken) {
      const now = Date.now();
      // Access tokens typically expire in 30 minutes, but we'll use Keycloak's info if available
      const accessTokenExpiry = keycloak.tokenParsed?.exp ? keycloak.tokenParsed.exp * 1000 : now + THIRTY_MINUTES_MS;

      await storageService.storeTokens({
        accessToken: keycloak.token,
        refreshToken: keycloak.refreshToken,
        idToken: keycloak.idToken || '',
        tokenParsed: keycloak.tokenParsed,
        accessTokenExpiry,
        refreshTokenExpiry: now + ONE_YEAR_MS,
        storageExpiry: now + THIRTY_DAYS_MS
      });
      await storageService.storeUserData(userInfo);
      console.info('[Auth] Tokens and user data stored successfully');
    }

    dispatch(
      AuthActions.loginSuccess({
        isAuthenticated: true,
        user: userInfo as KeycloakUserInfo,
        userType: (userInfo as KeycloakUserInfo).efka_access.role,
        tokenParsed: keycloak.tokenParsed,
        isBiometricEnabled: false,
        isBiometricAvailable: false,
        biometricType: undefined
      })
    );
  }, [dispatch]);

  /**
   * Handles OAuth login flow using InAppBrowser for secure authentication
   * This function implements a custom OAuth2 + PKCE flow that bypasses Keycloak's
   * built-in mobile handling to work around Capacitor/Cordova limitations:
   */
  const login = useCallback(async () => {
    const { codeVerifier, codeChallenge } = await generatePKCE(); // Generate cryptographically secure PKCE parameters

    const loginUrl = generateLoginUrl(codeChallenge); // Build OAuth login URL with PKCE parameters

    console.info('[Login] Built login URL:', loginUrl);

    // Open login page in InAppBrowser for secure authentication
    const browser = InAppBrowser.create(loginUrl, '_blank', IN_APP_BROWSER_OPTIONS);

    // Monitor URL changes to detect OAuth callback
    browser.on('loadstart').subscribe(async (event) => {
      if (!event.url.includes('capacitor://localhost')) return;

      console.info('[Login] OAuth callback received, finalizing authentication', JSON.stringify(event));

      try {
        const { code, state } = extractOAuthParams(event.url);

        if (!code || !state) throw new Error('[Login] Missing code or state in OAuth callback URL');

        if (code && state) {
          const tokens = await exchangeCodeForTokens({ code, codeVerifier });

          if (tokens.error) {
            console.error('[Login] Token exchange failed', tokens.error, tokens.error_description);
            browser.close();
            return;
          }

          if (tokens.access_token) {
            keycloak.token = tokens.access_token;
            keycloak.refreshToken = tokens.refresh_token;
            keycloak.idToken = tokens.id_token;
            keycloak.authenticated = true;

            if (tokens.id_token) {
              keycloak.tokenParsed = parseJwt(tokens.id_token) as KeycloakTokenParsed;
            }

            onAuthSuccess(); // Trigger successful authentication
            browser.close(); // Close browser after successful token exchange
          }
        }
      } catch (error) {
        console.error('[Login] Error processing callback URL:', error);
        browser.close();
      }
    });
  }, [onAuthSuccess]);

  // Handle token expiration by attempting refresh or forcing re-login
  const onTokenExpired = useCallback(() => {
    keycloak
      .updateToken(30) // Refresh if token will expire in 30 seconds
      .catch((error) => {
        console.error('Failed to refresh token', error);
        login();
      });
  }, [login]);

  // Restore authentication from stored tokens
  const restoreAuthFromStorage = useCallback(async () => {
    try {
      const storedTokens = await storageService.getStoredTokens();
      const storedUserData = await storageService.getStoredUserData();

      if (!storedTokens || !storedUserData) {
        console.info('[Auth] No stored tokens or user data found');
        return false;
      }

      // Check if biometrics are enabled
      const { isBiometricEnabled } = await import('@shared/services/biometrics-storage.service');
      const biometricsEnabled = await isBiometricEnabled();

      // Check if access token is expired
      const isAccessTokenExpired = Date.now() > storedTokens.accessTokenExpiry;

      // COLD START: If biometrics are NOT enabled, don't restore - require fresh login
      // This ensures security when app is completely closed and reopened
      if (!biometricsEnabled) {
        console.info('[Auth] Cold start: Biometrics disabled - clearing storage and requiring fresh login');
        await storageService.clearTokens();
        await storageService.clearUserData();
        return false;
      }

      keycloak.refreshToken = storedTokens.refreshToken;
      keycloak.idToken = storedTokens.idToken;
      keycloak.tokenParsed = storedTokens.tokenParsed as KeycloakTokenParsed;
      keycloak.authenticated = true;

      if (biometricsEnabled && isAccessTokenExpired) {
        try {
          // Use our custom refresh token function
          const tokens = await refreshAccessToken({ refreshToken: storedTokens.refreshToken });

          if (tokens.access_token && !tokens.error) {
            console.info('[Auth] Access token refreshed successfully (biometrics enabled)');

            // Update Keycloak instance with new tokens
            keycloak.token = tokens.access_token;
            keycloak.refreshToken = tokens.refresh_token || storedTokens.refreshToken;
            keycloak.idToken = tokens.id_token || storedTokens.idToken;

            // Parse the new access token
            if (tokens.access_token) {
              keycloak.tokenParsed = parseJwt(tokens.access_token) as KeycloakTokenParsed;
            }

            // Update stored tokens with new access token
            const now = Date.now();
            const newAccessTokenExpiry = keycloak.tokenParsed?.exp
              ? keycloak.tokenParsed.exp * 1000
              : now + THIRTY_MINUTES_MS;

            await storageService.storeTokens({
              accessToken: tokens.access_token,
              refreshToken: tokens.refresh_token || storedTokens.refreshToken,
              idToken: tokens.id_token || storedTokens.idToken,
              tokenParsed: keycloak.tokenParsed,
              accessTokenExpiry: newAccessTokenExpiry,
              refreshTokenExpiry: storedTokens.refreshTokenExpiry, // Keep original refresh expiry
              storageExpiry: storedTokens.storageExpiry // Keep original storage expiry
            });
          } else {
            console.error('[Auth] Token refresh failed:', tokens.error, tokens.error_description);
            // If refresh fails, clear stored tokens and return false to trigger login
            await storageService.clearTokens();
            await storageService.clearUserData();
            return false;
          }
        } catch (refreshError) {
          console.error('[Auth] Failed to refresh access token:', refreshError);
          // If refresh fails, clear stored tokens and return false to trigger login
          await storageService.clearTokens();
          await storageService.clearUserData();
          return false;
        }
      } else {
        keycloak.token = storedTokens.accessToken;
        console.info('[Auth] Access token is still valid, using stored token');
      }

      // Dispatch restored authentication state
      dispatch(
        AuthActions.loginSuccess({
          isAuthenticated: true,
          user: storedUserData as KeycloakUserInfo,
          userType: (storedUserData as KeycloakUserInfo).efka_access.role,
          tokenParsed: keycloak.tokenParsed as KeycloakTokenParsed,
          wasRestoredFromStorage: true // Mark as cold start restoration
        })
      );

      return true; // Successfully restored
    } catch (error) {
      console.error('[Auth] Failed to restore authentication from storage:', error);
      return false; // No stored tokens or restore failed
    }
  }, [dispatch]);

  const initKeycloak = useCallback(async () => {
    console.info('Initializing Keycloak...');

    // Try to restore authentication from storage first
    const restored = await restoreAuthFromStorage();

    if (restored) {
      console.info('[Keycloak] Authentication restored from storage');
      return;
    }

    // Override Keycloak's login method to use our custom PKCE implementation
    keycloak.login = login;

    // Initialize Keycloak with Cordova adapter and handle authentication state
    keycloak
      .init(KEYCLOAK_PROVIDER_INIT_OPTIONS)
      .then((authenticated) => {
        if (!authenticated) {
          console.info('[Keycloak] Not authenticated, will trigger login');
        }
      })
      .catch((error) => {
        console.error('Keycloak authentication failed', error);
        login(); // Fallback to login on initialization failure
      });
  }, [login, restoreAuthFromStorage]);

  // Complex logout flow for Capacitor: clear local state + server-side logout + reinitialize
  const logout = useCallback(async () => {
    console.info('[Logout] Starting complete logout, stored data cleared and reset process');

    // Clear stored tokens and user data
    await storageService.clearTokens();
    await storageService.clearUserData();

    dispatch(AuthActions.logout()); // Clear Redux auth state first

    //Perform server-side logout with id_token_hint for proper SSO logout
    const logoutUrl = generateLogoutUrl(`${keycloak.idToken}`);

    const browser = InAppBrowser.create(logoutUrl, '_blank', IN_APP_BROWSER_OPTIONS);

    // Monitor logout completion via custom callback scheme
    browser.on('loadstart').subscribe((event) => {
      console.info('[Logout] Loadstart URL:', event.url);

      if (!event.url.includes('efkadashboard://callback/logout')) return;

      browser.close();
      console.info('[Logout] Server logout complete, reinitializing for fresh login');

      // After server logout, create completely fresh Keycloak instance
      setTimeout(() => {
        resetAndReinitializeKeycloak();
        initKeycloak();
        keycloak.onTokenExpired = onTokenExpired;
        keycloak.onAuthSuccess = onAuthSuccess;
        keycloak.onAuthLogout = logout;
      }, 300); // Ensure cleanup completion
    });
  }, [dispatch, initKeycloak, onAuthSuccess, onTokenExpired]);

  // Initialize Keycloak on component mount with Capacitor-specific configuration
  useEffect(() => {
    if (keycloak.authenticated !== undefined) return; // Already initialized - skip duplicate initialization

    initKeycloak();
    // Set up event handlers for token lifecycle management
    keycloak.onTokenExpired = onTokenExpired;
    keycloak.onAuthSuccess = onAuthSuccess;
    keycloak.onAuthLogout = logout;
  }, [initKeycloak, logout, onAuthSuccess, onTokenExpired]);

  return { logout, login };
};
